import { useCallback } from 'react';

import {
	useTable,
	useFilters,
	useGlobalFilter,
	useColumnOrder,
	useGroupBy,
	useSortBy,
	useExpanded,
	useBlockLayout,
	useResizeColumns,
	Row
} from 'react-table';
import { useSticky } from 'react-table-sticky';
import type { FilterTypes, UseTableOptions } from 'react-table';

import {
	useFilteredRowSelect,
	useSelectionColumnForceFirst,
	useRowSelect,
	DEFAULT_COLUMN
} from 'bringg-web/components/bringg-react-table';
import { useSetColumnWidths } from 'bringg-web/components/bringg-react-table/plugin-hooks/use-set-column-widths';
import type { BaseRecord } from 'bringg-web/components/bringg-react-table';

interface UseAlertsTableProps<T extends BaseRecord>
	extends Omit<
		UseTableOptions<T>,
		| 'defaultColumn'
		| 'getRowId'
		| 'autoResetPage'
		| 'autoResetExpanded'
		| 'autoResetGroupBy'
		| 'autoResetSelectedRows'
		| 'autoResetSortBy'
		| 'autoResetFilters'
		| 'autoResetRowState'
		| 'autoResetHiddenColumns'
	> {
	filterTypes?: FilterTypes<T>;
	autoResetState?: boolean;
	getRowSelectable?(row: Row<T>): boolean;
}

function useAlertsTable<T extends BaseRecord>({
	autoResetState = false,
	getRowSelectable,
	...rest
}: UseAlertsTableProps<T>) {
	const getRowId = useCallback<UseTableOptions<T>['getRowId']>(
		(row, relativeIndex, parent) => (parent ? [parent.id, row.id].join('.') : String(row.id)),
		[]
	);

	return useTable<T>(
		{
			...rest,
			getRowId,
			defaultColumn: DEFAULT_COLUMN,
			autoResetPage: false, // according to doc only resets page to 1 after data changes for classic pagination
			autoResetExpanded: autoResetState,
			autoResetGroupBy: autoResetState,
			autoResetSelectedRows: autoResetState,
			autoResetSortBy: autoResetState,
			autoResetFilters: autoResetState,
			autoResetRowState: autoResetState,
			getRowSelectable
		},
		useFilters,
		useGlobalFilter,
		useColumnOrder,
		useGroupBy,
		useSortBy,
		useExpanded,
		useBlockLayout,
		useResizeColumns,
		useSticky,
		useSelectionColumnForceFirst,
		// useRowSelect was moved to app level so this hook can be modified
		useRowSelect,
		useFilteredRowSelect,
		useSetColumnWidths
	);
}

export default useAlertsTable;
